import './App.css'
import { BrowserRouter, Route, Routes, Navigate } from 'react-router-dom'
import { StyledEngineProvider, ThemeProvider, createTheme } from '@mui/material'
import Navbar from '../components/Navbar'
import Sidebar from '../components/Sidebar'
import { AppRoutes } from '../helpers/routes'
import { HomePage, LoginPage, RegisterPage } from '.'
import { SnackbarKey, SnackbarProvider } from 'notistack'
import { AuthContext as AuthProvider } from '../context/AuthContext'
import { CloseSnackbarButton } from '../helpers/utils'
import AuthOutlet from '@auth-kit/react-router/AuthOutlet'
import RecoverPasswordPage from './recoverAccount/RecoverPasswordPage'
import ActivateUserPage from './login/ActivateUserPage'
import UserPage from './user/UserPage'
import AdminsPage from './admins/AdminsPage'
import NewsPage from './news/NewsPage'
import PostsPage from './posts/PostsPage'
import ActivateLikedinUserPage from './login/ActivateLikedinUserPage'
import PendingUserPage from './register/PendingUserPage'
import GraduatesPage from './pendingUsers/GraduatesPage'
import SinglePostPage from './posts/SinglePostPage'
import MessagesPage from './messages/MessagesPage'
import CommunitiesPage from './communities/CommunitiesPage'
import CommunityPage from './communities/CommunityPage'
import JobPage from './job/JobPage'
import BenefitsPage from './benefits/BenefitsPage'
import CredentialPage from './credential/CredentialPage'
import ValidateCredential from './user/ValidateCredential'
import UserActivatedPage from './pendingUsers/UserActivatedPage'
import StatisticsPage from './statistics/StatisticsPage'
import {
    CategoryScale,
    Chart,
    Legend,
    LinearScale,
    LineElement,
    PointElement,
    Title,
    Tooltip,
    BarElement,
    ArcElement,
    Filler,
    RadialLinearScale,
} from 'chart.js'
import { PageContext } from '../context/PageContext'
import { useContext } from 'react'
import PendingPostsPage from './communities/PendingPostsPage'
import StatisticsProvider from '../context/StatisticsContext'
import StatisticsTendenciesPage from './statistics/tendencies/StatisticsTendenciesPage'
import StatisticsSuccessAreasPage from './statistics/successAreas/StatisticsSuccessAreasPage'
import StatisticsAcademicPage from './statistics/academic/StatisticsAcademicPage'
import StatisticsImprovementAreasPage from './statistics/improvementAreas/StatisticsImprovementAreasPage'

function App() {
    const navbarHeight = 'pb-[64px]'
    const { showSidebar, smallScreen } = useContext(PageContext)

    const theme = createTheme({
        typography: {
            fontFamily: 'Inter',
        },
    })

    const publicRoutes = [
        {
            path: AppRoutes.root,
            element: <Navigate to={AppRoutes.home} />,
        },
        {
            path: AppRoutes.login,
            element: <LoginPage />,
        },
        {
            path: AppRoutes.register,
            element: <RegisterPage />,
        },
        {
            path: AppRoutes.recoverPassword,
            element: <RecoverPasswordPage />,
        },
        {
            path: AppRoutes.validateMailUser,
            element: <ActivateUserPage />,
        },
        {
            path: AppRoutes.validateDocument,
            element: <UserActivatedPage />,
        },
        {
            path: AppRoutes.activateLikedinUser,
            element: <ActivateLikedinUserPage />,
        },
        {
            path: AppRoutes.pendingUser,
            element: <PendingUserPage />,
        },
        {
            path: AppRoutes.validateCredential,
            element: <ValidateCredential />,
        },
        {
            path: `${AppRoutes.validateCredential}/:token`,
            element: <ValidateCredential />,
        },
    ]

    const privateRoutes = [
        {
            path: AppRoutes.home,
            element: <HomePage />,
        },
        {
            path: AppRoutes.user,
            element: <UserPage />,
        },
        {
            path: AppRoutes.admins,
            element: <AdminsPage />,
        },
        {
            path: AppRoutes.posts,
            element: <PostsPage />,
        },
        {
            path: AppRoutes.news,
            element: <NewsPage />,
        },
        {
            path: AppRoutes.jobs,
            element: <JobPage />,
        },
        {
            path: AppRoutes.communities,
            element: <CommunitiesPage />,
        },
        {
            path: AppRoutes.community,
            element: <CommunityPage />,
        },
        {
            path: AppRoutes.pendingPosts,
            element: <PendingPostsPage />,
        },
        {
            path: AppRoutes.graduates,
            element: <GraduatesPage />,
        },
        {
            path: AppRoutes.singlePost,
            element: <SinglePostPage />,
        },
        {
            path: AppRoutes.benefits,
            element: <BenefitsPage />,
        },
        {
            path: AppRoutes.credential,
            element: <CredentialPage />,
        },
        {
            path: AppRoutes.messages,
            element: <MessagesPage />,
        },
        {
            path: AppRoutes.statistics.main,
            element: <StatisticsPage />,
        },
        {
            path: AppRoutes.statistics.tendencies,
            element: <StatisticsTendenciesPage />,
        },
        {
            path: AppRoutes.statistics.successAreas,
            element: <StatisticsSuccessAreasPage />,
        },
        {
            path: AppRoutes.statistics.improvementAreas,
            element: <StatisticsImprovementAreasPage />,
        },
        {
            path: AppRoutes.statistics.academic,
            element: <StatisticsAcademicPage />,
        },
    ]

    const snackbarAction = (bar: SnackbarKey) => <CloseSnackbarButton bar={bar} />

    Chart.register(
        CategoryScale,
        LinearScale,
        PointElement,
        LineElement,
        Title,
        Tooltip,
        Legend,
        BarElement,
        ArcElement,
        Filler,
        RadialLinearScale,
    )

    return (
        <StyledEngineProvider injectFirst>
            <ThemeProvider theme={theme}>
                <SnackbarProvider
                    maxSnack={10}
                    action={snackbarAction}
                >
                    <AuthProvider>
                        <StatisticsProvider>
                            <BrowserRouter>
                                <Navbar />
                                <div className={`flex min-h-[calc(100vh-64px)] h-dvh`}>
                                    <Sidebar />
                                    <div
                                        className={`${smallScreen ? (showSidebar ? 'w-0' : 'w-full p-5') : 'w-full p-10'} flex justify-start items-start md:pt-[28px] overflow-auto`}
                                    >
                                        <Routes>
                                            {publicRoutes.map((route) => (
                                                <Route
                                                    key={route.path}
                                                    path={route.path}
                                                    element={route.element}
                                                />
                                            ))}
                                            <Route element={<AuthOutlet fallbackPath="/login" />}>
                                                {privateRoutes.map((route) => (
                                                    <Route
                                                        key={route.path}
                                                        path={route.path}
                                                        element={
                                                            <div
                                                                className={`w-full flex justify-start items-start ${navbarHeight}`}
                                                            >
                                                                {route.element}
                                                            </div>
                                                        }
                                                    />
                                                ))}
                                            </Route>
                                            <Route
                                                path="*"
                                                element={<Navigate to={AppRoutes.home} />}
                                            />
                                        </Routes>
                                    </div>
                                </div>
                            </BrowserRouter>
                        </StatisticsProvider>
                    </AuthProvider>
                </SnackbarProvider>
            </ThemeProvider>
        </StyledEngineProvider>
    )
}

export default App
